import { useState, useEffect } from 'react';
import { useAuth } from '@/context';

export interface CreditPurchaseConfirmationState {
  isConfirmed: boolean;
  isProcessing: boolean;
  error: string | null;
}

/**
 * Hook to handle credit purchase confirmation after Stripe redirect
 *
 * Usage:
 * - Checks URL for credits_purchased parameter
 * - Refreshes user data to get updated credit balance
 * - Shows success message
 *
 * @returns Confirmation state
 */
export function useCreditPurchaseConfirmation() {
  const [state, setState] = useState<CreditPurchaseConfirmationState>({
    isConfirmed: false,
    isProcessing: false,
    error: null,
  });

  const { refreshUser } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const creditsPurchased = urlParams.get('credits_purchased');
    const sessionId = urlParams.get('session_id');

    // Only process if this is a credit purchase redirect
    if (creditsPurchased !== 'true' || !sessionId) {
      return;
    }

    const processConfirmation = async () => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        // Wait a moment for webhook to process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Refresh user data to get updated credit balance
        await refreshUser();

        // Clean up URL (remove query params)
        window.history.replaceState({}, document.title, window.location.pathname);

        setState({
          isConfirmed: true,
          isProcessing: false,
          error: null,
        });
      } catch (error) {
        console.error('Credit purchase confirmation failed:', error);
        setState({
          isConfirmed: false,
          isProcessing: false,
          error: error instanceof Error ? error.message : 'Failed to confirm credit purchase',
        });
      }
    };

    processConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - URL params are checked inside

  return state;
}
